/**
 * TUTORIAL SERVICE v1.0
 * Gestiona el progreso del tutorial y tooltips contextuales
 */

const TUTORIAL_STORAGE_KEY = 'mealcycle-tutorial-progress';

// ═══════════════════════════════════════════════
// DEFINICIÓN DE PASOS DEL TUTORIAL
// ═══════════════════════════════════════════════

export const TUTORIAL_STEPS = {
  // Onboarding inicial
  WELCOME: 'welcome',
  INTRO_COMPONENTS: 'intro_components',
  INTRO_RECIPES: 'intro_recipes',
  INTRO_PLANNER: 'intro_planner',
  INTRO_SHOPPING: 'intro_shopping',
  ONBOARDING_COMPLETE: 'onboarding_complete',

  // Tooltips contextuales por página
  COMPONENTS_FIRST_VISIT: 'components_first_visit',
  COMPONENTS_LOAD_EXAMPLES: 'components_load_examples',
  COMPONENTS_CREATE_MANUAL: 'components_create_manual',
  
  RECIPES_FIRST_VISIT: 'recipes_first_visit',
  RECIPES_LOAD_EXAMPLES: 'recipes_load_examples',
  RECIPES_CREATE_FIRST: 'recipes_create_first',
  
  PLANNER_FIRST_VISIT: 'planner_first_visit',
  PLANNER_ADD_MEAL: 'planner_add_meal',
  PLANNER_COOK_MEAL: 'planner_cook_meal',
  PLANNER_DUPLICATE_WEEK: 'planner_duplicate_week',
  
  SHOPPING_FIRST_VISIT: 'shopping_first_visit',
  SHOPPING_GENERATE_FROM_PLAN: 'shopping_generate_from_plan',
  SHOPPING_ADD_MANUAL: 'shopping_add_manual',
  SHOPPING_MARK_PURCHASED: 'shopping_mark_purchased',
  SHOPPING_ADD_TO_STOCK: 'shopping_add_to_stock',
  SHOPPING_PRICE_HISTORY: 'shopping_price_history',

  STOCK_FIRST_VISIT: 'stock_first_visit',
  STOCK_ADD_ITEM: 'stock_add_item',
};

// ═══════════════════════════════════════════════
// TOOLTIPS CONTEXTUALES POR PÁGINA
// ═══════════════════════════════════════════════

export const TOOLTIPS = {
  // Components Page
  [TUTORIAL_STEPS.COMPONENTS_FIRST_VISIT]: {
    id: TUTORIAL_STEPS.COMPONENTS_FIRST_VISIT,
    title: '🧩 Componentes',
    message: 'Los componentes son los platos individuales. Por ejemplo: "Milanesa", "Puré", "Ensalada".',
    placement: 'center',
    actions: [
      { label: 'Cargar Ejemplos', highlight: 'load-examples-btn', primary: true },
      { label: 'Crear Manual', highlight: 'create-component-btn' }
    ]
  },

  [TUTORIAL_STEPS.COMPONENTS_LOAD_EXAMPLES]: {
    id: TUTORIAL_STEPS.COMPONENTS_LOAD_EXAMPLES,
    title: '📦 Cargar Ejemplos',
    message: 'Cargá 150+ componentes argentinos listos para usar. Incluye carnes, guarniciones, salsas y más.',
    placement: 'bottom',
    targetId: 'load-examples-btn',
    actions: [
      { label: '¡Cargar!', primary: true, action: 'load-examples' }
    ]
  },

  // Recipes Page
  [TUTORIAL_STEPS.RECIPES_FIRST_VISIT]: {
    id: TUTORIAL_STEPS.RECIPES_FIRST_VISIT,
    title: '📖 Recetas',
    message: 'Combiná componentes para crear comidas completas. Por ejemplo: "Milanesa con Puré y Ensalada".',
    placement: 'center',
    actions: [
      { label: 'Cargar Ejemplos', highlight: 'load-recipes-btn', primary: true },
      { label: 'Crear Receta', highlight: 'create-recipe-btn' }
    ]
  },

  // Planner Page
  [TUTORIAL_STEPS.PLANNER_FIRST_VISIT]: {
    id: TUTORIAL_STEPS.PLANNER_FIRST_VISIT,
    title: '📅 Planificador',
    message: 'Organizá tu semana asignando recetas a cada comida. Tocá cualquier día para empezar.',
    placement: 'center',
    actions: [
      { label: 'Agregar Primera Comida', highlight: 'day-0', primary: true }
    ]
  },

  [TUTORIAL_STEPS.PLANNER_ADD_MEAL]: {
    id: TUTORIAL_STEPS.PLANNER_ADD_MEAL,
    title: '➕ Agregar Comida',
    message: 'Elegí una receta o componente para este momento del día.',
    placement: 'center',
    targetId: 'meal-selector',
    actions: [
      { label: 'Seleccionar', primary: true }
    ]
  },

  [TUTORIAL_STEPS.PLANNER_COOK_MEAL]: {
    id: TUTORIAL_STEPS.PLANNER_COOK_MEAL,
    title: '🔥 Marcar como Cocinado',
    message: 'Cuando cocines, la app descontará automáticamente los ingredientes del stock.',
    placement: 'bottom',
    targetId: 'cook-meal-btn',
    actions: [
      { label: 'Cocinar', primary: true, action: 'cook-meal' }
    ]
  },

  [TUTORIAL_STEPS.PLANNER_DUPLICATE_WEEK]: {
    id: TUTORIAL_STEPS.PLANNER_DUPLICATE_WEEK,
    title: '📋 Duplicar Semana',
    message: '¿Te gustó cómo organizaste esta semana? Copiala completa a la próxima.',
    placement: 'bottom',
    targetId: 'week-menu-btn',
    actions: [
      { label: 'Ver Opciones', primary: true }
    ]
  },

  // Shopping Page
  [TUTORIAL_STEPS.SHOPPING_FIRST_VISIT]: {
    id: TUTORIAL_STEPS.SHOPPING_FIRST_VISIT,
    title: '🛒 Lista de Compras',
    message: 'Generá automáticamente tu lista desde el plan semanal, o creá una manual.',
    placement: 'center',
    actions: [
      { label: 'Desde Plan', highlight: 'generate-from-plan-btn', primary: true },
      { label: 'Manual', highlight: 'create-list-btn' }
    ]
  },

  [TUTORIAL_STEPS.SHOPPING_GENERATE_FROM_PLAN]: {
    id: TUTORIAL_STEPS.SHOPPING_GENERATE_FROM_PLAN,
    title: '📅 → 🛒 Magia',
    message: 'Seleccioná un rango de fechas y la app calculará qué te falta comprar.',
    placement: 'bottom',
    targetId: 'generate-from-plan-btn',
    actions: [
      { label: 'Generar', primary: true }
    ]
  },

  [TUTORIAL_STEPS.SHOPPING_PRICE_HISTORY]: {
    id: TUTORIAL_STEPS.SHOPPING_PRICE_HISTORY,
    title: '💰 Historial de Precios',
    message: 'Tocá el ícono 📊 para ver cómo varió el precio de este producto.',
    placement: 'left',
    targetId: 'price-history-btn',
    actions: [
      { label: 'Ver Historial', primary: true }
    ]
  },

  [TUTORIAL_STEPS.SHOPPING_ADD_TO_STOCK]: {
    id: TUTORIAL_STEPS.SHOPPING_ADD_TO_STOCK,
    title: '✅ → 📦 Agregar al Stock',
    message: 'Cuando termines de comprar, pasá todo al stock con un toque.',
    placement: 'bottom',
    targetId: 'add-to-stock-btn',
    actions: [
      { label: 'Agregar', primary: true }
    ]
  },

  // Stock Page
  [TUTORIAL_STEPS.STOCK_FIRST_VISIT]: {
    id: TUTORIAL_STEPS.STOCK_FIRST_VISIT,
    title: '📦 Stock',
    message: 'Tu inventario de alimentos. Se descuenta automáticamente al cocinar.',
    placement: 'center',
    actions: [
      { label: 'Agregar Item', highlight: 'add-stock-btn', primary: true }
    ]
  },
};

// ═══════════════════════════════════════════════
// API DEL SERVICIO
// ═══════════════════════════════════════════════

class TutorialService {
  constructor() {
    this.progress = this.loadProgress();
  }

  /**
   * Cargar progreso del localStorage
   */
  loadProgress() {
    try {
      const data = localStorage.getItem(TUTORIAL_STORAGE_KEY);
      return data ? JSON.parse(data) : {
        completedSteps: [],
        onboardingCompleted: false,
        currentStep: null,
        lastShownTooltip: null,
        tooltipDismissals: 0,
        startedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error loading tutorial progress:', error);
      return {
        completedSteps: [],
        onboardingCompleted: false,
        currentStep: null,
        lastShownTooltip: null,
        tooltipDismissals: 0,
        startedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Guardar progreso en localStorage
   */
  saveProgress() {
    try {
      localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(this.progress));
    } catch (error) {
      console.error('Error saving tutorial progress:', error);
    }
  }

  /**
   * Marcar un paso como completado
   */
  completeStep(stepId) {
    if (!this.progress.completedSteps.includes(stepId)) {
      this.progress.completedSteps.push(stepId);
      this.saveProgress();
      console.log(`✅ Tutorial step completed: ${stepId}`);
    }
  }

  /**
   * Verificar si un paso está completado
   */
  isStepCompleted(stepId) {
    return this.progress.completedSteps.includes(stepId);
  }

  /**
   * Marcar onboarding como completado
   */
  completeOnboarding() {
    this.progress.onboardingCompleted = true;
    this.completeStep(TUTORIAL_STEPS.ONBOARDING_COMPLETE);
    this.saveProgress();
    console.log('🎉 Onboarding completed!');
  }

  /**
   * Verificar si el onboarding está completo
   */
  isOnboardingCompleted() {
    return this.progress.onboardingCompleted;
  }

  /**
   * Obtener el siguiente tooltip a mostrar para una página
   */
  getNextTooltipForPage(page) {
    const pageSteps = Object.keys(TOOLTIPS).filter(key => key.includes(page));
    
    for (const stepId of pageSteps) {
      if (!this.isStepCompleted(stepId)) {
        return TOOLTIPS[stepId];
      }
    }
    
    return null;
  }

  /**
   * Descartar tooltip (máximo 3 veces antes de marcar como completado)
   */
  dismissTooltip(stepId) {
    this.progress.tooltipDismissals++;
    this.progress.lastShownTooltip = stepId;
    
    if (this.progress.tooltipDismissals >= 3) {
      this.completeStep(stepId);
    }
    
    this.saveProgress();
  }

  /**
   * Resetear tutorial completo
   */
  reset() {
    this.progress = {
      completedSteps: [],
      onboardingCompleted: false,
      currentStep: null,
      lastShownTooltip: null,
      tooltipDismissals: 0,
      startedAt: new Date().toISOString()
    };
    this.saveProgress();
    console.log('🔄 Tutorial reset');
  }

  /**
   * Obtener estadísticas del tutorial
   */
  getStats() {
    return {
      totalSteps: Object.keys(TUTORIAL_STEPS).length,
      completedSteps: this.progress.completedSteps.length,
      completionPercentage: (this.progress.completedSteps.length / Object.keys(TUTORIAL_STEPS).length) * 100,
      onboardingCompleted: this.progress.onboardingCompleted,
      startedAt: this.progress.startedAt
    };
  }
}

export default new TutorialService();